import { useMemo, useState, type ReactNode } from "react";
import {
  ComposableMap,
  Geographies,
  Geography,
  ZoomableGroup,
} from "react-simple-maps";
// Bundled locally (50m resolution) instead of fetched from a CDN at
// runtime — also the lowest resolution that still includes micro-states
// and dependent territories (Guernsey, Monaco, Vatican, etc.).
import worldAtlas50m from "world-atlas/countries-50m.json";

// Some ISO 3166-1 codes a country resolver can legitimately produce (e.g.
// Gibraltar, French overseas departments) have no shape in this topojson
// resolution — computed once so we can tell "unresolved country" apart from
// "resolved but not drawable" in the unmapped-items count below.
const RENDERABLE_COUNTRY_IDS = new Set(
  worldAtlas50m.objects.countries.geometries
    .map((g) => g.id)
    .filter((id): id is string => typeof id === "string"),
);

const NO_DATA_COLOR = "#E5E7EB";
// Sequential scale endpoints (Tailwind blue-100 → blue-900).
const SCALE_START = { r: 219, g: 234, b: 254 };
const SCALE_END = { r: 30, g: 58, b: 138 };
// Floor so a country with just 1 item is still visibly blue, not
// indistinguishable from the light end of the scale.
const MIN_INTENSITY = 0.18;

function lerp(a: number, b: number, t: number): number {
  return Math.round(a + (b - a) * t);
}

function getSequentialColor(count: number, maxCount: number): string {
  if (count <= 0 || maxCount <= 0) return NO_DATA_COLOR;
  // Log scale instead of linear: this data is very skewed (one country can
  // dominate all others), so a linear ratio crushes every non-max country
  // into the same near-white shade. log1p keeps small counts distinguishable
  // while still saturating towards the max.
  const ratio = Math.log1p(count) / Math.log1p(maxCount);
  const t = MIN_INTENSITY + ratio * (1 - MIN_INTENSITY);
  const r = lerp(SCALE_START.r, SCALE_END.r, t);
  const g = lerp(SCALE_START.g, SCALE_END.g, t);
  const b = lerp(SCALE_START.b, SCALE_END.b, t);
  return `rgb(${r}, ${g}, ${b})`;
}

export interface WorldMapItem {
  name: string;
  // ISO 3166-1 numeric code (matches topojson `geo.id`), or null when the
  // backend couldn't resolve the raw country value for this item.
  countryIsoNumeric: string | null;
}

interface WorldMapChoroplethProps {
  title: string;
  items: WorldMapItem[];
  unitSingular: string;
  unitPlural: string;
  headerExtra?: ReactNode;
  isLoading?: boolean;
  isError?: boolean;
}

type Tooltip = { name: string; count: number; x: number; y: number };
type ActiveCountry = { name: string; items: string[] };

export default function WorldMapChoropleth({
  title,
  items,
  unitSingular,
  unitPlural,
  headerExtra,
  isLoading = false,
  isError = false,
}: WorldMapChoroplethProps) {
  const [tooltip, setTooltip] = useState<Tooltip | null>(null);
  const [activeCountry, setActiveCountry] = useState<ActiveCountry | null>(
    null,
  );

  const namesByIsoNumeric = useMemo(() => {
    const byCountry = new Map<string, string[]>();
    for (const item of items) {
      if (!item.countryIsoNumeric) continue;
      const list = byCountry.get(item.countryIsoNumeric) ?? [];
      list.push(item.name);
      byCountry.set(item.countryIsoNumeric, list);
    }
    return byCountry;
  }, [items]);

  const unmappedCount = useMemo(
    () =>
      items.filter(
        (item) =>
          !item.countryIsoNumeric ||
          !RENDERABLE_COUNTRY_IDS.has(item.countryIsoNumeric),
      ).length,
    [items],
  );

  const maxCount = useMemo(() => {
    return Math.max(
      0,
      ...[...namesByIsoNumeric.values()].map((list) => list.length),
    );
  }, [namesByIsoNumeric]);

  const unitLabel = (count: number) =>
    count === 1 ? unitSingular : unitPlural;

  return (
    <section>
      <div className="mb-4 flex items-center gap-4">
        <h2 className="flex items-center gap-2 text-xs font-bold uppercase tracking-widest text-gray-500 before:h-px before:w-6 before:bg-gray-300">
          {title}
        </h2>
        {headerExtra}
      </div>
      <div className="flex items-start gap-4">
        <div className="flex-1">
          <div className="relative h-[70vh] min-h-[520px] rounded-xl border border-gray-100 bg-gradient-to-b from-sky-50 to-white p-5 shadow-sm">
            {(isLoading || isError || items.length === 0) && (
              <div className="absolute inset-0 z-10 flex items-center justify-center rounded-xl bg-white/70 text-sm font-medium text-gray-500">
                {isError
                  ? "Couldn't load map data — please retry."
                  : isLoading
                    ? "Loading map…"
                    : "No data"}
              </div>
            )}
            <ComposableMap
              className="h-full w-full"
              projectionConfig={{ scale: 145 }}
            >
              <ZoomableGroup zoom={1} minZoom={1} maxZoom={8}>
                <Geographies geography={worldAtlas50m}>
                  {({ geographies }) =>
                    geographies.map((geo) => {
                      const name = geo.properties?.name ?? "Unknown";
                      const countryItems =
                        namesByIsoNumeric.get(geo.id) ?? [];
                      const fill = getSequentialColor(
                        countryItems.length,
                        maxCount,
                      );
                      return (
                        <Geography
                          key={geo.rsmKey}
                          geography={geo}
                          onClick={() =>
                            setActiveCountry({ name, items: countryItems })
                          }
                          onMouseEnter={(event) => {
                            setTooltip({
                              name,
                              count: countryItems.length,
                              x: event.clientX,
                              y: event.clientY,
                            });
                          }}
                          onMouseMove={(event) => {
                            setTooltip((prev) =>
                              prev
                                ? {
                                    ...prev,
                                    x: event.clientX,
                                    y: event.clientY,
                                  }
                                : prev,
                            );
                          }}
                          onMouseLeave={() => setTooltip(null)}
                          style={{
                            default: {
                              fill,
                              stroke: "#FFFFFF",
                              strokeWidth: 0.5,
                              outline: "none",
                            },
                            hover: {
                              fill,
                              stroke: "#FFFFFF",
                              strokeWidth: 0.5,
                              outline: "none",
                              cursor: "pointer",
                              filter: "brightness(0.85)",
                            },
                            pressed: {
                              fill,
                              stroke: "#FFFFFF",
                              strokeWidth: 0.5,
                              outline: "none",
                            },
                          }}
                        />
                      );
                    })
                  }
                </Geographies>
              </ZoomableGroup>
            </ComposableMap>
            {tooltip && (
              <div
                className="pointer-events-none fixed z-50 -translate-x-1/2 -translate-y-full rounded-md bg-gray-900 px-2 py-1 text-xs font-medium text-white shadow-md"
                style={{ left: tooltip.x, top: tooltip.y - 8 }}
              >
                {tooltip.name} — {tooltip.count} {unitLabel(tooltip.count)}
              </div>
            )}
          </div>
          <div className="mt-3 flex items-center gap-2 text-xs text-gray-500">
            <span>0</span>
            <span
              className="h-3 w-40 rounded-sm"
              style={{
                background: `linear-gradient(to right, rgb(${SCALE_START.r}, ${SCALE_START.g}, ${SCALE_START.b}), rgb(${SCALE_END.r}, ${SCALE_END.g}, ${SCALE_END.b}))`,
              }}
            />
            <span>
              {maxCount} {unitLabel(maxCount)} (max)
            </span>
            {unmappedCount > 0 && (
              <span className="ml-auto text-gray-400">
                {unmappedCount} {unitLabel(unmappedCount)} without a
                resolved country (not shown)
              </span>
            )}
          </div>
        </div>

        {/* Click detail panel — same pattern as the Portfolio Breakdown pie charts */}
        <div className="w-48 shrink-0">
          {activeCountry ? (
            <div className="rounded-lg border border-gray-200 bg-gray-50 p-3">
              <p className="text-xs font-bold text-gray-700 mb-2 border-b border-gray-200 pb-1">
                {activeCountry.name}
              </p>
              <p className="text-[10px] text-gray-400 mb-1">
                {activeCountry.items.length}{" "}
                {unitLabel(activeCountry.items.length)}
              </p>
              <ul className="space-y-0.5 max-h-52 overflow-y-auto">
                {activeCountry.items.map((name, i) => (
                  <li
                    key={i}
                    className="text-[11px] text-gray-600 truncate"
                    title={name}
                  >
                    {name}
                  </li>
                ))}
              </ul>
            </div>
          ) : (
            <p className="text-[11px] text-gray-400 mt-4">
              Click a country to see {unitPlural}
            </p>
          )}
        </div>
      </div>
    </section>
  );
}
